import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refresh();
      toast.success("Password updated");
      nav("/dashboard", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2));
    }
    setBusy(false);
  };

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-zinc-50 grid lg:grid-cols-2" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="hidden lg:flex bg-zinc-900 text-white p-12 flex-col justify-between">
        <div>
          <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            KPA<span className="text-blue-400">.</span>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mt-1">Performance Manager</div>
        </div>
        <div>
          <KeyRound className="w-10 h-10 text-blue-400 mb-6" strokeWidth={1.5} />
          <h1 className="text-4xl font-bold tracking-tight leading-[1.1]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Set your<br /> own password.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md text-sm leading-relaxed">
            Welcome, {user?.name}. You signed in with a temporary password issued by your administrator. Choose a personal password to continue.
          </p>
        </div>
        <div className="text-xs text-zinc-500">Tip: at least 8 characters, mix letters and numbers.</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">First sign-in</div>
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Change your password
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Signed in as <b>{user?.email}</b></p>

          <div className="mt-8 space-y-4">
            <Field label="Temporary password">
              <input type="password" required autoFocus value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900" />
            </Field>
            <Field label="New password">
              <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900" />
            </Field>
            <Field label="Confirm new password">
              <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900" />
            </Field>

            <button type="submit" disabled={busy} data-testid="cp-submit-btn" className="w-full bg-zinc-900 text-white py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-60">
              {busy ? "Updating…" : "Set new password"}
            </button>

            <button type="button" onClick={handleLogout} data-testid="cp-logout-btn" className="w-full flex items-center justify-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 pt-2">
              <LogOut className="w-3 h-3" /> Sign out instead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
